import type { Group, Sheet, Point, Pouch } from '../types';
import { GROUP_COLORS } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { updateSheet } from './SheetLogic';
import React from 'react';

// Z-indexの定数定義
const MIN_GROUP_Z_INDEX = 100001;
const MAX_GROUP_Z_INDEX = 199999;

// 最小グループサイズの定数
export const MIN_GROUP_SIZE = 100; // ピクセル

// グリッドスナップの定数（25px間隔の小さい点）
export const GRID_SIZE = 25;

// グローバルなスナップ有効状態（デフォルトはオン）
// window.__isSnapEnabled で参照可能
declare global {
  interface Window {
    __isSnapEnabled?: boolean;
  }
}

// 初期値をtrueに設定
if (typeof window !== 'undefined' && window.__isSnapEnabled === undefined) {
  window.__isSnapEnabled = true;
}

/**
 * 座標をグリッドにスナップする
 * @param value スナップする座標値
 * @param gridSize グリッドサイズ（デフォルト: 25px）
 * @returns スナップされた座標値（スナップが無効の場合は元の値）
 */
export const snapToGrid = (value: number, gridSize: number = GRID_SIZE): number => {
  // スナップが無効の場合は元の値をそのまま返す
  if (typeof window !== 'undefined' && window.__isSnapEnabled === false) {
    return value;
  }
  return Math.round(value / gridSize) * gridSize;
};

/**
 * SVGパスを点の配列から生成する
 * @param points パスを構成する点の配列
 * @returns SVGパス文字列
 */
export const generatePathFromPoints = (points: Point[]): string => {
  if (points.length < 2) return '';
  
  // 最初の点から始まるパスを作成
  let path = `M${points[0].x},${points[0].y} `;
  
  // 2点目から最後までを線で結ぶ
  for (let i = 1; i < points.length; i++) {
    path += `L${points[i].x},${points[i].y} `;
  }
  
  // パスを閉じる
  path += 'Z';
  
  return path;
};

/**
 * 角丸四角形のSVGパスを生成する
 * @param startX 開始X座標
 * @param startY 開始Y座標
 * @param endX 終了X座標
 * @param endY 終了Y座標
 * @param radius 角の丸みの半径
 * @returns SVGパス文字列
 */
export const generateRoundedRectPath = (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  radius: number = 10
): string => {
  // 左上と右下の座標を計算（整数に丸める）
  const left = Math.round(Math.min(startX, endX));
  const top = Math.round(Math.min(startY, endY));
  const width = Math.round(Math.abs(endX - startX));
  const height = Math.round(Math.abs(endY - startY));
  
  // 最小サイズチェック
  if (width < 20 || height < 20) {
    return `M ${left} ${top} L ${left + Math.max(width, 20)} ${top} L ${left + Math.max(width, 20)} ${top + Math.max(height, 20)} L ${left} ${top + Math.max(height, 20)} Z`;
  }
  
  // radiusが大きすぎる場合は調整
  const maxRadius = Math.min(width / 2, height / 2, radius);
  const safeRadius = Math.max(0, Math.round(maxRadius));

  // シンプルな角丸四角形のパス（整数座標のみ使用）
  const right = left + width;
  const bottom = top + height;
  
  const path = [
    `M ${left + safeRadius} ${top}`,
    `L ${right - safeRadius} ${top}`,
    `Q ${right} ${top} ${right} ${top + safeRadius}`,
    `L ${right} ${bottom - safeRadius}`,
    `Q ${right} ${bottom} ${right - safeRadius} ${bottom}`,
    `L ${left + safeRadius} ${bottom}`,
    `Q ${left} ${bottom} ${left} ${bottom - safeRadius}`,
    `L ${left} ${top + safeRadius}`,
    `Q ${left} ${top} ${left + safeRadius} ${top}`,
    'Z'
  ].join(' ');

  return path;
};

/**
 * 新しいグループを作成する
 * @param path グループのSVGパス
 * @param pouchIds グループに含まれるパウチのID配列
 * @param maxZIndex 現在の最大Z-Index
 * @param sheet 現在のシート（最後のカラーを取得するため）
 * @returns 作成したグループオブジェクト
 */
export const createGroup = (
  path: string, 
  pouchIds: string[] = [], 
  maxZIndex: number,
  sheet?: Sheet,
  defaulCcolor?: string,
  placeholderIndex?: number
): Group => {
  // 新しいzIndexを計算（範囲内に収める）
  const newZIndex = Math.max(MIN_GROUP_Z_INDEX, Math.min(maxZIndex + 1, MAX_GROUP_Z_INDEX));
  
  // シートから最後のカラーを取得、なければデフォルト値（yellow）を使用
  const color = defaulCcolor ?? GROUP_COLORS[0].id;
  
  return {
    id: uuidv4(),
    path,
    pouches: pouchIds,
    color,
    placeholderText: placeholderIndex !== undefined ? `Group ${placeholderIndex + 1}` : `Group`,
    _animateSubtotal: true
  };
};

/**
 * グループを更新する
 * @param groupId 更新するグループのID
 * @param updates 更新内容（Groupの部分オブジェクト）
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updateGroup = async (
  groupId: string, 
  updates: Partial<Group>, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  const updatedGroups = activeSheet.groups.map(g => 
    g.id === groupId ? { ...g, ...updates } : g
  );
  
  // カラーの更新があるかどうかを確認
  const hasColorUpdate = updates.color !== undefined;
  // 更新後のグループの色を取得
  const newColor = hasColorUpdate ? 
    (updates.color === 'mute' ? '#666666' : (updates.color || GROUP_COLORS[0].id)) : 
    undefined;
  
  const updatedSheet = {
    ...activeSheet,
    groups: updatedGroups,
    lastModified: new Date(),
    // カラーが更新された場合はlastGroupColorを更新（カラーIDを保存）
    ...(hasColorUpdate && { 
      lastGroupColor: newColor
    })
  };
  
  await updateSheet(updatedSheet, dispatch);
};

/**
 * グループのパスを更新する（リサイズ時など）
 * @param groupId 更新するグループのID
 * @param path 新しいSVGパス
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updateGroupPath = async (
  groupId: string, 
  path: string, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updateGroup(groupId, { path }, activeSheet, dispatch);
};

/**
 * グループの色を更新する
 * @param groupId 更新するグループのID
 * @param color 新しい色コード
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updateGroupColor = async (
  groupId: string, 
  color: string, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updateGroup(groupId, { color }, activeSheet, dispatch);
};

/**
 * グループにパウチを追加する
 * @param groupId 更新するグループのID
 * @param pouchIds 追加するパウチのID配列
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const addPouchesToGroup = async (
  groupId: string, 
  pouchIds: string[], 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  const group = activeSheet.groups.find(g => g.id === groupId);
  if (!group) return;
  
  // 重複しないように新しいパウチIDを追加
  // const newPouches = [...new Set([...group.pouches, ...pouchIds])];
  const newPouches: string[] = [...group.pouches];
  for (const id of pouchIds) {
    if (!newPouches.includes(id)) {
      newPouches.push(id);
    }
  }
  
  await updateGroup(
    groupId, 
    { 
      pouches: newPouches,
      _animateSubtotal: true
    }, 
    activeSheet, 
    dispatch
  );
  
  // パウチの更新は不要（グループのpouchesプロパティのみで管理）
};

/**
 * グループからパウチを削除する
 * @param groupId 更新するグループのID
 * @param pouchIds 削除するパウチのID配列
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const removePouchesFromGroup = async (
  groupId: string, 
  pouchIds: string[], 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  const group = activeSheet.groups.find(g => g.id === groupId);
  if (!group) return;
  
  // グループからパウチを削除
  const updatedPouches = group.pouches.filter(id => !pouchIds.includes(id));
  
  await updateGroup(
    groupId, 
    { 
      pouches: updatedPouches,
      _animateSubtotal: true
    }, 
    activeSheet, 
    dispatch
  );
  
  // パウチの更新は不要（グループのpouchesプロパティのみで管理）
};

/**
 * グループを削除する
 * @param groupId 削除するグループのID
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const deleteGroup = async (
  groupId: string, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  // グループを削除
  const updatedGroups = activeSheet.groups.filter(g => g.id !== groupId);
  
  const updatedSheet = {
    ...activeSheet,
    groups: updatedGroups,
    lastModified: new Date()
  };
  
  await updateSheet(updatedSheet, dispatch);
};

/**
 * グループとその中身（所属パウチと内包グループ）をすべて削除する
 * @param groupId 削除するグループのID
 * @param groups 全グループのリスト
 * @param pouches 全パウチのリスト
 * @param deletePouches パウチも削除するかどうか（デフォルト: true）
 * @param keepMainGroup メイングループを残すかどうか（デフォルト: false）
 * @returns { 
 *   pouchesToDelete: 削除すべきパウチのID配列,
 *   groupsToDelete: 削除すべきグループのID配列
 * }
 */
export const deleteGroupWithContents = (
  groupId: string,
  groups: Group[],
  pouches: Array<{ 
    id: string; 
    x: number; 
    y: number; 
    value: number; 
    isNegative: boolean; 
    multiplier: number;
    zIndex?: number;
    color?: string;
    _isDeleting?: boolean;
  }>,
  deletePouches: boolean = true,
  keepMainGroup: boolean = false
): { 
  pouchesToDelete: string[];
  groupsToDelete: string[];
} => {
  // 対象のグループを取得
  const targetGroup = groups.find(g => g.id === groupId);
  if (!targetGroup) {
    console.error('削除対象のグループが見つかりません:', groupId);
    return { pouchesToDelete: [], groupsToDelete: [] };
  }
  
  // グループ内に完全に含まれる他のグループを特定
  const containedGroups = getContainedGroups(targetGroup, groups);
  const allGroupsToDelete = keepMainGroup 
    ? containedGroups.map(g => g.id)  // メイングループを除く
    : [groupId, ...containedGroups.map(g => g.id)];  // メイングループも含める
  
  // 削除対象のパウチを特定
  const pouchesInGroup = pouches.filter(p => {
    if (keepMainGroup) {
      // メイングループを残す場合：メイングループに所属するパウチ + 内包グループに所属するパウチ
      const isInMainGroup = targetGroup.pouches && targetGroup.pouches.includes(p.id);
      const isInContainedGroups = containedGroups.some(cg => 
        cg.pouches && cg.pouches.includes(p.id)
      );
      const shouldDelete = isInMainGroup || isInContainedGroups;
      return shouldDelete;
    } else {
      // メイングループも削除する場合：すべての削除対象グループに所属するパウチ
      return allGroupsToDelete.some(groupId => {
        const group = groups.find(g => g.id === groupId);
        return group && group.pouches && group.pouches.includes(p.id);
      });
    }
  });
  
  // 削除対象のIDを収集
  const pouchIdsToDelete = deletePouches ? pouchesInGroup.map(p => p.id) : [];
  const groupIdsToDelete = allGroupsToDelete;
  
  return {
    pouchesToDelete: pouchIdsToDelete,
    groupsToDelete: groupIdsToDelete
  };
};

/**
 * SVGパスの境界ボックスを取得する
 * @param path SVGパス文字列
 * @returns 境界ボックス（left, top, right, bottom）または境界が取得できない場合はnull
 */
// getPathBounds用のキャッシュ
const getPathBoundsCache = new Map<string, { left: number; top: number; right: number; bottom: number } | null>();

// キャッシュをクリアする関数
export const clearGroupLogicCache = () => {
  shouldShowTotalLabelCache.clear();
  isGroupPhysicallyContainedCache.clear();
  getPathBoundsCache.clear();
};

// 特定のグループに関連するキャッシュのみをクリアする関数
export const clearGroupRelatedCache = (groupId?: string) => {
  if (groupId) {
    // 特定のグループに関連するキャッシュエントリを削除
    const shouldShowKeys = Array.from(shouldShowTotalLabelCache.keys());
    shouldShowKeys.forEach(key => {
      if (key.includes(groupId)) {
        shouldShowTotalLabelCache.delete(key);
      }
    });
    
    const isContainedKeys = Array.from(isGroupPhysicallyContainedCache.keys());
    isContainedKeys.forEach(key => {
      if (key.includes(groupId)) {
        isGroupPhysicallyContainedCache.delete(key);
      }
    });
  } else {
    // グループIDが指定されていない場合は全キャッシュをクリア
    clearGroupLogicCache();
  }
};

// パウチの値が変更されたときにキャッシュをクリアする関数
export const clearPouchRelatedCache = (pouchId?: string) => {
  if (pouchId) {
    // パウチに関連するキャッシュエントリを削除
    const shouldShowKeys = Array.from(shouldShowTotalLabelCache.keys());
    shouldShowKeys.forEach(key => {
      if (key.includes(pouchId)) {
        shouldShowTotalLabelCache.delete(key);
      }
    });
  } else {
    // パウチIDが指定されていない場合は全キャッシュをクリア
    clearGroupLogicCache();
  }
};

// GTP関連の変更時にキャッシュをクリアする関数
export const clearGTPRelatedCache = () => {
  // GTP関連の変更時は全キャッシュをクリア（安全のため）
  clearGroupLogicCache();
  // groupCacheもクリア（シート切り替え時の問題を防ぐため）
  groupCache.clear();
};

// グループリサイズ時にキャッシュをクリアする関数
export const clearGroupResizeCache = (groupId?: string) => {
  // グループリサイズ時は確実に全キャッシュをクリア（安全のため）
  // リサイズ時は境界計算が重要なので、キャッシュを完全にクリア
  clearGroupLogicCache();
};

export const getPathBounds = (path: string): { left: number; top: number; right: number; bottom: number } | null => {
  // パスが空または無効な場合はnullを返す
  if (!path || typeof path !== 'string') {
    console.warn('getPathBounds: Empty or invalid path:', path);
    getPathBoundsCache.set(path, null);
    return null;
  }
  
  // キャッシュから結果を取得
  if (getPathBoundsCache.has(path)) {
    return getPathBoundsCache.get(path)!;
  }
  
  try {
    // パスの基本的な検証（無効な文字が含まれていないかチェック）
    if (!/^[MLHVCSQTAZmlhvcsqtaz\s\d.,\-+eE]+$/.test(path)) {
      console.warn('getPathBounds: Invalid characters in path:', path);
      getPathBoundsCache.set(path, null);
      return null;
    }
    
    // パスに問題のある数値が含まれていないかチェック
    const problematicNumbers = path.match(/-?\d+\.?\d*[eE][+-]?\d+/g);
    if (problematicNumbers && problematicNumbers.length > 0) {
      console.warn('getPathBounds: Path contains exponential notation:', path, 'Problematic numbers:', problematicNumbers);
      getPathBoundsCache.set(path, null);
      return null;
    }
    
    // 一時的なSVG要素を作成してDOMに追加
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.style.position = 'absolute';
    svg.style.visibility = 'hidden';
    
    // パス要素を作成
    const pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    
    try {
      pathElement.setAttribute('d', path);
    } catch (setAttributeError) {
      console.error('getPathBounds: Error setting path attribute:', setAttributeError, 'Path:', path);
      document.body.removeChild(svg);
      getPathBoundsCache.set(path, null);
      return null;
    }
    
    svg.appendChild(pathElement);
    
    // DOMに追加して境界ボックスを取得
    document.body.appendChild(svg);
    
    let bbox;
    try {
      bbox = pathElement.getBBox();
    } catch (getBBoxError) {
      console.error('getPathBounds: Error getting bbox:', getBBoxError, 'Path:', path);
      document.body.removeChild(svg);
      getPathBoundsCache.set(path, null);
      return null;
    }
    
    document.body.removeChild(svg);
    
    // 境界ボックスが有効かチェック
    if (!isFinite(bbox.x) || !isFinite(bbox.y) || !isFinite(bbox.width) || !isFinite(bbox.height)) {
      console.warn('getPathBounds: Invalid bbox values:', bbox, 'Path:', path);
      getPathBoundsCache.set(path, null);
      return null;
    }
    
    // 境界ボックスを返す
    const result = {
      left: bbox.x,
      top: bbox.y,
      right: bbox.x + bbox.width,
      bottom: bbox.y + bbox.height
    };
    
    getPathBoundsCache.set(path, result);
    return result;
  } catch (error) {
    console.error('getPathBounds: Unexpected error:', error, 'Path:', path);
    getPathBoundsCache.set(path, null);
    return null;
  }
};

/**
 * 点がSVGパス内にあるかどうか判定する
 * @param path SVGパス文字列
 * @param x 判定するX座標
 * @param y 判定するY座標
 * @returns パス内にある場合はtrue、そうでない場合はfalse
 */
export const isPointInPath = (path: string, x: number, y: number): boolean => {
  
  try {
    // バウンディングボックスを先に取得して、明らかに範囲外なら早期リターン
    const bounds = getPathBounds(path);
    if (!bounds) return false;
    
    // バウンディングボックスの中に点があるか確認
    const isInBounds = 
      x >= bounds.left && 
      x <= bounds.right && 
      y >= bounds.top && 
      y <= bounds.bottom;
    
    if (!isInBounds) {
      
      return false;
    }
    
    // 現時点ではバウンディングボックスでのみ判定する（単純化）
    // これは完全に正確ではありませんが、パフォーマンス上のトレードオフとして許容します
    // 角丸四角形の場合、ほとんどの場合でこれで十分です
    
    // 必要に応じて、より正確な判定を追加することができます
    // 例えば、多角形の各辺との交差判定を行うなど
    
    
    return true;
  } catch (error) {
    console.error('Error checking if point is in path:', error);
    return false;
  }
};

/**
 * SVGパスを指定した量だけ移動させる
 * @param path 元のSVGパス文字列
 * @param deltaX X方向の移動量
 * @param deltaY Y方向の移動量
 * @returns 移動後のSVGパス文字列
 */
export const translatePath = (path: string, deltaX: number, deltaY: number): string => {
  // パスが空の場合はそのまま返す
  if (!path) return path;
  
  // 数値を適切な文字列にフォーマットする関数
  const formatNumber = (num: number): string => {
    // NaN、Infinity、-Infinityの場合は0を返す
    if (!isFinite(num)) {
      console.warn('Invalid number in translatePath:', num);
      return '0';
    }
    // 極端に大きな数値の場合は制限する
    const maxValue = 1000000;
    const minValue = -1000000;
    const clampedNum = Math.max(minValue, Math.min(maxValue, num));
    
    // 極端に小さな数値（指数表記になる値）の場合は0に置き換える
    if (Math.abs(clampedNum) < 0.001) {
      return '0';
    }
    
    // 整数に丸めて指数表記を完全に防ぐ
    const rounded = Math.round(clampedNum);
    
    // 指数表記が含まれている場合は0に置き換える
    const result = rounded.toString();
    if (result.includes('e') || result.includes('E')) {
      return '0';
    }
    
    return result;
  };
  
  try {
    // パス文字列を走査して座標値を移動
    // SVGパスコマンドの正規表現
    const commandRegex = /([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi;
    const numberRegex = /-?\d+(\.\d+)?([eE][+-]?\d+)?/g;
    
    // 変換処理
    let translatedPath = path.replace(commandRegex, (match, command, params) => {
      const upperCommand = command.toUpperCase();
      
      // A（楕円弧）コマンドは特別な処理が必要
      if (upperCommand === 'A') {
        // rx ry x-axis-rotation large-arc-flag sweep-flag x y
        const numbers = params.match(numberRegex);
        if (numbers && numbers.length >= 7) {
          const x = parseFloat(numbers[5]) + deltaX;
          const y = parseFloat(numbers[6]) + deltaY;
          numbers[5] = formatNumber(x);
          numbers[6] = formatNumber(y);
          return command + numbers.join(' ');
        }
        return match;
      }
      
      // H（水平線）は X座標のみ
      if (upperCommand === 'H') {
        const numbers = params.match(numberRegex);
        if (numbers) {
          return command + numbers.map((n: string) => formatNumber(parseFloat(n) + deltaX)).join(' ');
        }
        return match;
      }
      
      // V（垂直線）は Y座標のみ
      if (upperCommand === 'V') {
        const numbers = params.match(numberRegex);
        if (numbers) {
          return command + numbers.map((n: string) => formatNumber(parseFloat(n) + deltaY)).join(' ');
        }
        return match;
      }
      
      // Z（閉じる）コマンドにはパラメータなし
      if (upperCommand === 'Z') {
        return command;
      }
      
      // その他のコマンド（M, L, C, S, Q, T）はx,y座標のペア
      const numbers = params.match(numberRegex);
      if (numbers) {
        const translatedNumbers = [];
        for (let i = 0; i < numbers.length; i++) {
          const num = parseFloat(numbers[i]);
          if (i % 2 === 0) { // X座標
            translatedNumbers.push(formatNumber(num + deltaX));
          } else { // Y座標
            translatedNumbers.push(formatNumber(num + deltaY));
          }
        }
        return command + translatedNumbers.join(' ');
      }
      
      return match;
    });
    
    return translatedPath;
  } catch (error) {
    console.error('Error translating path:', error);
    return path; // エラーが発生した場合は元のパスを返す
  }
};

/**
 * SVGパスが正常かどうかをチェックする関数
 * @param path チェック対象のSVGパス文字列
 * @returns 正常な場合はtrue、異常な場合はfalse
 */
const isPathValid = (path: string): boolean => {
  try {
    // 基本的な文字列検証
    if (!path || typeof path !== 'string' || path.length === 0) {
      return false;
    }
    
    // 無効な文字が含まれていないかチェック
    if (!/^[MLHVCSQTAZmlhvcsqtaz\s\d.,\-+eE]+$/.test(path)) {
      return false;
    }
    
    // 指数表記が含まれていないかチェック
    if (path.includes('e') || path.includes('E')) {
      return false;
    }
    
    // SVGパーサーで実際にテスト
    const testSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const testPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    testPath.setAttribute('d', path);
    testSvg.appendChild(testPath);
    document.body.appendChild(testSvg);
    const bbox = testPath.getBBox();
    document.body.removeChild(testSvg);
    
    // 境界ボックスが有効かチェック
    if (!isFinite(bbox.x) || !isFinite(bbox.y) || !isFinite(bbox.width) || !isFinite(bbox.height)) {
      return false;
    }
    
    // サイズが0でないかチェック
    if (bbox.width <= 0 || bbox.height <= 0) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * SVGパスを修復する関数
 * @param path 修復対象のSVGパス文字列
 * @returns 修復されたSVGパス文字列
 */
export const repairPath = (path: string): string => {
  if (!path || typeof path !== 'string') {
    console.warn('repairPath: Empty or invalid path input:', path);
    return '';
  }
  
  // パスが既に正常かどうかをチェック
  if (isPathValid(path)) {
    return path;
  }
  
  try {
    // 数値を適切な文字列にフォーマットする関数
    const formatNumber = (num: number): string => {
      // NaN、Infinity、-Infinityの場合は0を返す
      if (!isFinite(num)) {
        return '0';
      }
      // 極端に大きな数値の場合は制限する
      const maxValue = 1000000;
      const minValue = -1000000;
      const clampedNum = Math.max(minValue, Math.min(maxValue, num));
      
      // 極端に小さな数値（指数表記になる値）の場合は0に置き換える
      if (Math.abs(clampedNum) < 0.000001) {
        return '0';
      }
      
      // 小数点以下6桁までに制限し、不要な末尾の0を削除
      const formatted = clampedNum.toFixed(6).replace(/\.?0+$/, '');
      
      // 指数表記が含まれている場合は0に置き換える
      if (formatted.includes('e') || formatted.includes('E')) {
        return '0';
      }
      
      return formatted;
    };
    
    // 角丸四角形パスを再構築する関数
    const rebuildRoundedRectPath = (path: string): string => {
      const numberRegex = /-?\d+(\.\d+)?([eE][+-]?\d+)?/g;
      const allNumbers = path.match(numberRegex);
      
      if (!allNumbers || allNumbers.length < 8) {
        console.warn('repairPath: Not enough numbers for rounded rectangle:', allNumbers?.length);
        return '';
      }
      
      // 数値を修復して数値配列に変換
      const repairedNumbers = allNumbers.map(n => parseFloat(formatNumber(parseFloat(n))));
      
      // 有効な数値のみを抽出（NaNや異常値を除外）
      const validNumbers = repairedNumbers.filter(n => isFinite(n) && Math.abs(n) < 1000000);
      
      if (validNumbers.length < 8) {
        console.warn('repairPath: Not enough valid numbers for rounded rectangle:', validNumbers.length);
        return '';
      }
      
      // 境界ボックスを計算
      const minX = Math.min(...validNumbers.filter((_, i) => i % 2 === 0)); // 偶数インデックス（X座標）
      const maxX = Math.max(...validNumbers.filter((_, i) => i % 2 === 0));
      const minY = Math.min(...validNumbers.filter((_, i) => i % 2 === 1)); // 奇数インデックス（Y座標）
      const maxY = Math.max(...validNumbers.filter((_, i) => i % 2 === 1));
      
      // 角丸の半径を固定で10pxに設定
      const radius = 10;
      
      // 正しい角丸四角形を生成
      const result = 
        `M${minX} ${minY + radius}` +
        `L${minX} ${maxY - radius}` +
        `Q${minX} ${maxY} ${minX + radius} ${maxY}` +
        `L${maxX - radius} ${maxY}` +
        `Q${maxX} ${maxY} ${maxX} ${maxY - radius}` +
        `L${maxX} ${minY + radius}` +
        `Q${maxX} ${minY} ${maxX - radius} ${minY}` +
        `L${minX + radius} ${minY}` +
        `Q${minX} ${minY} ${minX} ${minY + radius}` +
        `Z`;
      
      return result;
    };
    
    // まず角丸四角形として再構築を試行
    const rebuiltPath = rebuildRoundedRectPath(path);
    if (rebuiltPath) {
      // 再構築されたパスが有効かテスト
      try {
        const testSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const testPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        testPath.setAttribute('d', rebuiltPath);
        testSvg.appendChild(testPath);
        document.body.appendChild(testSvg);
        const bbox = testPath.getBBox();
        document.body.removeChild(testSvg);
        
        if (isFinite(bbox.x) && isFinite(bbox.y) && isFinite(bbox.width) && isFinite(bbox.height)) {
          return rebuiltPath;
        }
      } catch (testError) {
        console.warn('repairPath: Rounded rectangle rebuild failed:', testError);
      }
    }
    
    // 角丸四角形の再構築に失敗した場合は、従来の修復方法を使用
    const commandRegex = /([MLHVCSQTAZ])([^MLHVCSQTAZ]*)/gi;
    const numberRegex = /-?\d+(\.\d+)?([eE][+-]?\d+)?/g;
    
    let repairedPath = path.replace(commandRegex, (match, command, params) => {
      const upperCommand = command.toUpperCase();
      
      // Z（閉じる）コマンドにはパラメータなし
      if (upperCommand === 'Z') {
        return command;
      }
      
      // その他のコマンドの数値を修復
      const numbers = params.match(numberRegex);
      if (numbers) {
        const repairedNumbers = numbers.map((n: string) => formatNumber(parseFloat(n)));
        
        // コマンド別の引数数制限
        let limitedNumbers = repairedNumbers;
        switch (upperCommand) {
          case 'M': case 'L': case 'T':
            // 2つずつのペア（x, y）
            limitedNumbers = repairedNumbers.slice(0, Math.floor(repairedNumbers.length / 2) * 2);
            break;
          case 'C':
            // 6つずつのセット（x1, y1, x2, y2, x, y）
            limitedNumbers = repairedNumbers.slice(0, Math.floor(repairedNumbers.length / 6) * 6);
            break;
          case 'S': case 'Q':
            // 4つずつのセット（x1, y1, x, y）
            limitedNumbers = repairedNumbers.slice(0, Math.floor(repairedNumbers.length / 4) * 4);
            break;
          case 'H':
            // 1つずつ（x座標のみ）
            limitedNumbers = repairedNumbers.slice(0, 1);
            break;
          case 'V':
            // 1つずつ（y座標のみ）
            limitedNumbers = repairedNumbers.slice(0, 1);
            break;
        }
        
        return command + limitedNumbers.join(' ');
      }
      
      return match;
    });
    
    
    // 修復後のパスが有効かチェック
    if (repairedPath && repairedPath.length > 0) {
      // 基本的なパス検証
      if (!/^[MLHVCSQTAZmlhvcsqtaz\s\d.,\-+eE]+$/.test(repairedPath)) {
        console.warn('repairPath: Repaired path contains invalid characters:', repairedPath);
        return '';
      }
      
      // 指数表記が含まれていないかチェック
      if (repairedPath.includes('e') || repairedPath.includes('E')) {
        console.warn('repairPath: Repaired path still contains exponential notation:', repairedPath);
        return '';
      }
      
      // SVGパーサーで実際にテスト
      try {
        const testSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const testPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        testPath.setAttribute('d', repairedPath);
        testSvg.appendChild(testPath);
        document.body.appendChild(testSvg);
        const bbox = testPath.getBBox();
        document.body.removeChild(testSvg);
        
        if (!isFinite(bbox.x) || !isFinite(bbox.y) || !isFinite(bbox.width) || !isFinite(bbox.height)) {
          console.warn('repairPath: Repaired path produces invalid bbox:', bbox, 'Path:', repairedPath);
          return '';
        }
      } catch (testError) {
        console.warn('repairPath: Traditional repair failed SVG test:', testError, 'Path:', repairedPath);
        return '';
      }
    } else {
      console.warn('repairPath: Repaired path is empty');
    }
    
    return repairedPath;
  } catch (error) {
    console.error('Error repairing path:', error);
    return '';
  }
};

/**
 * グループのパスを修復する関数
 * @param group 修復対象のグループ
 * @returns 修復されたグループ
 */
export const repairGroupPath = (group: Group): Group => {
  const repairedPath = repairPath(group.path);
  
  if (repairedPath !== group.path) {
    if (repairedPath === '') {
      console.error('repairGroupPath: Group path repair failed, using fallback:', group.id, 'Original:', group.path);
      // パス修復に失敗した場合は、デフォルトの小さな四角形パスを使用
      const fallbackPath = 'M0 0L100 0L100 100L0 100Z';
      return { ...group, path: fallbackPath };
    } else {
      console.warn('repairGroupPath: Group path repaired:', group.id, 'Original:', group.path, 'Repaired:', repairedPath);
      
      // 修復後のパスが有効かテスト
      const testBounds = getPathBounds(repairedPath);
      if (!testBounds) {
        console.error('repairGroupPath: Repaired path is still invalid:', group.id, 'Repaired path:', repairedPath);
        const fallbackPath = 'M0 0L100 0L100 100L0 100Z';
        return { ...group, path: fallbackPath };
      }
      
      return { ...group, path: repairedPath };
    }
  }
  return group;
};

/**
 * 複数のグループのパスを修復する関数
 * @param groups 修復対象のグループ配列
 * @returns 修復されたグループ配列
 */
export const repairGroupPaths = (groups: Group[]): Group[] => {
  return groups.map(repairGroupPath);
};

/**
 * グループ描画開始
 * @param x 開始X座標
 * @param y 開始Y座標
 * @returns 開始点の座標（グリッドにスナップ）
 */
export const startGroupDrawing = (x: number, y: number): Point => {
  return { 
    x: snapToGrid(x), 
    y: snapToGrid(y) 
  };
};

/**
 * グループ描画継続
 * @param startPoint 開始点の座標
 * @param currentX 現在のX座標
 * @param currentY 現在のY座標
 * @returns 角丸四角形のSVGパス（終点はグリッドにスナップ）
 */
export const continueGroupDrawing = (startPoint: Point, currentX: number, currentY: number): string => {
  const snappedX = snapToGrid(currentX);
  const snappedY = snapToGrid(currentY);
  return generateRoundedRectPath(startPoint.x, startPoint.y, snappedX, snappedY);
};

/**
 * グループ描画完了
 * @param startPoint 開始点の座標
 * @param endX 終了X座標
 * @param endY 終了Y座標
 * @param pouches パウチの配列
 * @param maxGroupZIndex 現在の最大グループZ-Index
 * @param color 色
 * @param sheet 現在のシート（最後のカラーを取得するため）
 * @param isTooSmall グループが最小サイズ未満かどうか（trueなら作成しない）
 * @param placeholderIndex グループのプレースホルダーテキストのインデックス
 * @returns 作成されたグループまたはnull（最小サイズ未満の場合）
 */
export const finishGroupDrawing = (
  startPoint: Point,
  endX: number,
  endY: number,
  pouches: Array<{ id: string; x: number; y: number }>,
  maxGroupZIndex: number,
  color?: string,
  sheet?: Sheet,
  isTooSmall: boolean = false,
  placeholderIndex?: number
): Group | null => {
  // 最小サイズのチェック（外部から渡された判定を使用）
  if (isTooSmall) return null;
  
  // 終点をグリッドにスナップ
  const snappedEndX = snapToGrid(endX);
  const snappedEndY = snapToGrid(endY);
  
  // 角丸四角形のパスを作成（スナップされた座標を使用）
  const path = generateRoundedRectPath(startPoint.x, startPoint.y, snappedEndX, snappedEndY);
  
  // パス内に含まれるパウチのIDを取得
  const pouchesInPath = pouches.filter(pouch => {
    return isPointInPath(path, pouch.x, pouch.y);
  }).map(pouch => pouch.id);
  
  // グループを作成して返す
  return createGroup(path, pouchesInPath, maxGroupZIndex, sheet, color, placeholderIndex);
};

/**
 * グループを前面に出す関数
 * @param groupId 前面に出すグループのID
 * @param groups 現在のグループ配列
 * @param maxGroupZIndex 現在の最大Z-Index
 * @returns { updatedGroups: グループ配列, newMaxGroupZIndex: 更新後の最大Z-Index }
 */
export const bringGroupToFront = (
  groupId: string,
  groups: Group[],
  maxGroupZIndex: number
): { updatedGroups: Group[], newMaxGroupZIndex: number } => {
  // 上限しきい値に近づいたらz-indexを圧縮
  if (maxGroupZIndex >= MAX_GROUP_Z_INDEX - 100) {
    // グループをID順にソート（z-indexはDOM操作で管理するため、ID順を使用）
    const sortedGroups = [...groups].sort((a, b) => 
      a.id.localeCompare(b.id)
    );
    
    // z-indexはDOM操作で管理するため、グループオブジェクトは変更しない
    const updatedGroups = sortedGroups;
    
    // 対象のグループの新しいz-indexはDOM操作で管理されるため、固定値を返す
    const newZIndex = 100001;
    
    // z-indexはDOM操作で管理するため、グループオブジェクトは変更しない
    const finalGroups = updatedGroups;
    
    return {
      updatedGroups: finalGroups,
      newMaxGroupZIndex: newZIndex
    };
  }
  
  // 通常のz-index更新処理
  // z-indexの新しい値を計算（現在の最大値+1、ただし上限はMAX_GROUP_Z_INDEX）
  const newZIndex = Math.min(maxGroupZIndex + 1, MAX_GROUP_Z_INDEX);
  
  // グループのz-indexを更新
  const updatedGroups = groups.map(group => 
    group.id === groupId ? { ...group, zIndex: newZIndex } : group
  );
  
  return {
    updatedGroups,
    newMaxGroupZIndex: newZIndex
  };
};

/**
 * パウチがグループ内に物理的に含まれているか判定する
 * @param path グループのSVGパス
 * @param pouch パウチオブジェクト
 * @returns パウチの中心点がパス内にある場合はtrue
 */
export const isPouchInGroup = (
  path: string,
  pouch: { x: number; y: number }
): boolean => {
  return isPointInPath(path, pouch.x, pouch.y);
};

/**
 * グループ内のパウチの合計値を計算する
 * @param groupId グループID
 * @param pouches パウチの配列
 * @param groups グループの配列（オプション）
 * @returns グループ内のパウチの合計値
 */
// キャッシュ用のMap（メモリリークを防ぐため、適切なタイミングでクリアする）
const groupCache = new Map<string, Group>();
const pathBoundsCache = new Map<string, any>();

// キャッシュクリア関数
export const clearGroupSubtotalCache = () => {
  groupCache.clear();
  pathBoundsCache.clear();
};

// キャッシュ更新関数（グループが変更された時に呼び出す）
export const updateGroupSubtotalCache = (groups: Group[]) => {
  groupCache.clear();
  groups.forEach(group => groupCache.set(group.id, group));
};

// パフォーマンステスト用のベンチマーク関数
export const benchmarkCalculateGroupSubtotal = (
  groupId: string,
  pouches: Array<{ id: string; value: number; isNegative: boolean; multiplier: number; isGroupTotalPouch?: boolean; referencedGroupId?: string; x?: number; y?: number; }>,
  groups: Group[] = [],
  iterations: number = 1000
): { averageTime: number; totalTime: number } => {
  const startTime = performance.now();
  
  for (let i = 0; i < iterations; i++) {
    calculateGroupSubtotal(groupId, pouches, groups);
  }
  
  const endTime = performance.now();
  const totalTime = endTime - startTime;
  const averageTime = totalTime / iterations;
  
  
  return { averageTime, totalTime };
};

export const calculateGroupSubtotal = (
  groupId: string,
  pouches: Array<{ id: string; value: number; isNegative: boolean; multiplier: number; isGroupTotalPouch?: boolean; referencedGroupId?: string; x?: number; y?: number; }>,
  groups: Group[] = []
): number => {  
  // 早期リターン: パウチが空の場合
  if (pouches.length === 0) return 0;
  
  // 早期リターン: パウチが1つで通常パウチの場合
  if (pouches.length === 1 && !pouches[0].isGroupTotalPouch) {
    const pouch = pouches[0];
    const actualValue = pouch.multiplier < 0 
      ? pouch.value / Math.abs(pouch.multiplier)
      : pouch.value * pouch.multiplier;
    return Math.round(actualValue * 1000000000) / 1000000000;
  }

  // グループのキャッシュを作成（初回のみ、またはグループが変更された場合）
  // キャッシュが空、サイズが違う、または目的のグループIDがキャッシュにない場合は再構築
  const needsRebuild = groupCache.size === 0 || 
                       groupCache.size !== groups.length ||
                       !groupCache.has(groupId);
  
  if (needsRebuild) {
    groupCache.clear();
    groups.forEach(group => groupCache.set(group.id, group));
  }
  
  // ミュートグループを事前に取得
  const muteGroups = groups.filter(g => g.color === 'mute');
  const muteGroupIds = new Set(muteGroups.map(g => g.id));
  
  // 現在のグループをキャッシュから取得
  const currentGroup = groupCache.get(groupId);
  if (!currentGroup) return 0;
  
  // パウチIDに対応するパウチを見つけて合計を計算
  // 自分自身を参照するGTPと外包グループのGTPは除外（循環参照を防ぐため）
  const result = pouches
    .filter(pouch => {
      // 通常パウチの処理
      if (!pouch.isGroupTotalPouch) {
        // ミュートグループに所属するパウチを除外
        return !muteGroups.some(g => g.pouches?.includes(pouch.id));
      }
      
      // GTPの場合、自分自身を参照していないもののみ含める
      if (pouch.referencedGroupId === groupId) {
        return false; // 自分自身を参照するGTPは除外
      }
      
      // 外包グループのGTPを除外（循環参照を防ぐため）
      const referencedGroup = groupCache.get(pouch.referencedGroupId || '');
      
      if (referencedGroup && currentGroup) {
        // 参照先グループのパウチを事前にフィルタリング
        const referencedGroupPouches = pouches.filter(p => 
          referencedGroup.pouches?.includes(p.id) && 
          p.x !== undefined && p.y !== undefined
        );
        
        // 参照先グループのパウチが現在のグループのパス内に含まれているかで判定
        const isReferencedGroupInner = referencedGroupPouches.some(p => 
          isPointInPath(currentGroup.path, p.x!, p.y!)
        );
        if (isReferencedGroupInner) {
          return true; // 内包グループのGTPは含める
        }
        
        // 現在のグループのパウチを事前にフィルタリング
        const currentGroupPouches = pouches.filter(p => 
          currentGroup.pouches?.includes(p.id) && 
          p.x !== undefined && p.y !== undefined
        );
        
        // 現在のグループのパウチが参照先グループのパス内に含まれている場合は除外
        const isReferencedGroupOuter = currentGroupPouches.some(p => 
          isPointInPath(referencedGroup.path, p.x!, p.y!)
        );
        if (isReferencedGroupOuter) {
          return false; // 外包グループのGTPは除外
        }
      }
      
      return true; // 内包グループのGTPは含める
    })
    .reduce((total, pouch) => {
      // 乗数が負数の場合は割り算モード
      let actualValue;
      if (pouch.multiplier < 0) {
        actualValue = pouch.value / Math.abs(pouch.multiplier);
      } else {
        actualValue = pouch.value * pouch.multiplier;
      }
      return total + actualValue;
    }, 0);
  return Math.round(result * 1000000000) / 1000000000;
};

/**
 * グループの条件判定ユーティリティ
 * @param group 対象のグループ
 * @param pathBounds グループのパス境界領域
 * @param allGroups 全グループの一覧
 * @param pouches 全パウチのリスト
 * @returns isMergeDisabled マージボタンが無効化されるべきかの判定結果
 */
export const isGroupMergeDisabled = (
  group: Group,
  pathBounds: { left: number; top: number; right: number; bottom: number } | null,
  allGroups: Group[],
  pouches: Array<{ id: string; multiplier: number }>
): boolean => {
  // パスの境界がない場合はマージボタンを無効化
  if (!pathBounds) return true;
  
  // グループに所属するパウチをチェック
  const groupPouches = pouches.filter(p => group.pouches?.includes(p.id));
  const hasAssociatedPouches = groupPouches.length > 0;
  
  // グループ内に完全に含まれる他のグループをチェック
  const containedGroups = allGroups.filter(g => {
    if (g.id === group.id) return false; // 自分自身は除外
    
    // グループのパスから境界を取得
    const targetBounds = pathBounds;
    const otherBounds = getPathBounds(g.path);
    
    if (!targetBounds || !otherBounds) return false;
    
    // 完全に内包されているかチェック
    return (
      otherBounds.left >= targetBounds.left &&
      otherBounds.right <= targetBounds.right &&
      otherBounds.top >= targetBounds.top &&
      otherBounds.bottom <= targetBounds.bottom
    );
  });
  
  // 所属パウチも内包グループもない場合にtrueを返す
  return !hasAssociatedPouches && containedGroups.length === 0;
};

/**
 * グループ内に完全に含まれる（内包されている）グループを取得する
 * @param targetGroup 対象のグループ
 * @param allGroups 全グループのリスト
 * @returns 内包されているグループの配列
 */
export const getContainedGroups = (
  targetGroup: Group,
  allGroups: Group[]
): Group[] => {
  // 対象グループのパス境界を取得
  const targetBounds = getPathBounds(targetGroup.path);
  if (!targetBounds) return [];
  
  // 完全に内包されているグループをフィルタリング
  return allGroups.filter(g => {
    if (g.id === targetGroup.id) return false; // 自分自身は除外
    
    // グループのパスから境界を取得
    const otherBounds = getPathBounds(g.path);
    if (!otherBounds) return false;
    
    // 完全に内包されているかチェック
    return (
      otherBounds.left >= targetBounds.left &&
      otherBounds.right <= targetBounds.right &&
      otherBounds.top >= targetBounds.top &&
      otherBounds.bottom <= targetBounds.bottom
    );
  });
};

// グループの色からパウチの色を取得する関数
export const getMatchingPouchColor = (groupColor: string): string => {
  const colorMap: { [key: string]: string } = {
    'gray': 'gray',
    'yellow': 'orange',
    'green': 'yellow-green',
    'blue': 'sky-blue',
    'purple': 'red-purple',
    'red': 'pink'
  };
  return colorMap[groupColor] || 'gray'; // デフォルトはグレー
};



/**
 * グループのカラーを次の色に変更する
 * @param groupId 変更するグループのID
 * @param groups 現在のグループリスト
 * @returns カラーが変更された新しいグループリスト
 */
export const changeGroupColor = (
  groupId: string,
  groups: Group[],
  reverse: boolean = false,
  skipMute: boolean = false
): Group[] => {
  // 変更対象のグループを見つける
  const targetGroup = groups.find(g => g.id === groupId);
  if (!targetGroup) return groups;
  
  // 現在の色を見つける
  const currentColor = targetGroup.color || GROUP_COLORS[0].id;
  
  // 特殊なミュートグループの処理
  if (currentColor === GROUP_COLORS[GROUP_COLORS.length - 1].id && !reverse && !skipMute) {
    // 最後の色からミュートへ（逆ローテーションでない場合のみ、skipMuteでない場合のみ）
    return groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          color: 'mute'
        };
      }
      return group;
    });
  } else if (currentColor === 'mute') {
    // ミュートから最初の色へ（逆ローテーションの場合は最後の色へ）
    return groups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          color: reverse ? GROUP_COLORS[GROUP_COLORS.length - 1].id : GROUP_COLORS[0].id
        };
      }
      return group;
    });
  }
  
  // 通常の色の切り替え
  const currentColorIndex = GROUP_COLORS.findIndex(color => color.id === currentColor);
  let nextColorIndex: number;
  
  if (reverse) {
    // 逆ローテーション
    if (currentColorIndex === 0 && !skipMute) {
      // イエロー（最初の色）から逆方向に行く場合は、ミュート（skipMuteでない場合のみ）
      return groups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            color: 'mute'
          };
        }
        return group;
      });
    } else if (currentColorIndex <= 0) {
      // skipMute時、または最初の色の場合は最後の色へラップ
      nextColorIndex = GROUP_COLORS.length - 1;
    } else if (currentColorIndex === GROUP_COLORS.length - 1) {
      // グレー（最後の色）から逆方向に行く場合は、その前の色（レッドなど）
      nextColorIndex = currentColorIndex - 1;
    } else {
      // その他の色から逆方向に行く場合
      nextColorIndex = currentColorIndex - 1;
    }
  } else {
    // 通常のローテーション（skipMute時は最後の色から最初の色へラップ）
    nextColorIndex = (currentColorIndex + 1) % GROUP_COLORS.length;
  }
  
  const nextColor = currentColorIndex === -1 
    ? GROUP_COLORS[0].id
    : GROUP_COLORS[nextColorIndex].id;
  
  return groups.map(group => {
    if (group.id === groupId) {
      return {
        ...group,
        color: nextColor
      };
    }
    return group;
  });
};

/**
 * グループを複製する
 * @param group 複製元のグループ
 * @param deltaX X方向の移動量
 * @param deltaY Y方向の移動量
 * @param maxZIndex 現在の最大Z-index
 * @returns 複製されたグループ
 */
export const cloneGroup = (group: Group, deltaX: number, deltaY: number, maxZIndex: number): Group => {
  // パスを移動
  const newPath = translatePath(group.path, deltaX, deltaY);
  
  // 元のグループから必要なプロパティだけを抽出し、新しい値で上書き
  const cloned: Group = {
    id: uuidv4(),
    path: newPath,
    pouches: [], // パウチ所属は明示的に空配列
    color: group.color,
    title: group.title,
    _animateSubtotal: true
  };
  
  return cloned;
};

/**
 * グループ複製時にGTPの参照を新しいグループに付け替える
 * @param pouches パウチ配列
 * @param groupIdMap 古いグループID → 新しいグループIDのマッピング
 * @param clonedPouchIds 複製されたパウチのIDリスト（複製されたGTPのみを対象にするため）
 * @returns 参照を更新したパウチ配列
 */
export const updateGTPReferencesAfterClone = (pouches: Pouch[], groupIdMap: {[oldId: string]: string}, clonedPouchIds: string[]): Pouch[] => {
  return pouches.map(pouch => {
    // 複製されたGTPで、参照しているグループが複製された場合のみ参照を更新
    if (pouch.isGroupTotalPouch && 
        pouch.referencedGroupId && 
        groupIdMap[pouch.referencedGroupId] && 
        clonedPouchIds.includes(pouch.id)) {
      return {
        ...pouch,
        referencedGroupId: groupIdMap[pouch.referencedGroupId]
      };
    }
    return pouch;
  });
};

/**
 * あるグループが別のグループに物理的に完全に内包されているかを判定する
 * @param containedGroupId 内包されているかを確認するグループのID
 * @param containerGroupId 内包しているかを確認するグループのID
 * @param groups グループの配列
 * @returns 完全に内包されている場合はtrue
 */
// isGroupPhysicallyContained用のキャッシュ
const isGroupPhysicallyContainedCache = new Map<string, boolean>();

export const isGroupPhysicallyContained = (containedGroupId: string, containerGroupId: string, groups: Group[]): boolean => {
  // キャッシュキーを生成
  const key = `${containedGroupId}-${containerGroupId}-${groups.map(g => `${g.id}:${g.path}`).join(',')}`;
  
  // キャッシュから結果を取得
  if (isGroupPhysicallyContainedCache.has(key)) {
    return isGroupPhysicallyContainedCache.get(key)!;
  }
  
  const containedGroup = groups.find(g => g.id === containedGroupId);
  const containerGroup = groups.find(g => g.id === containerGroupId);
  
  if (!containedGroup || !containerGroup) {
    isGroupPhysicallyContainedCache.set(key, false);
    return false;
  }
  
  // 両方のグループの境界を取得
  const containedBounds = getPathBounds(containedGroup.path);
  const containerBounds = getPathBounds(containerGroup.path);
  
  if (!containedBounds || !containerBounds) {
    isGroupPhysicallyContainedCache.set(key, false);
    return false;
  }
  
  // 内包されているグループの境界が、内包しているグループの境界内に完全に収まっているかを確認
  const result = (
    containedBounds.left > containerBounds.left &&
    containedBounds.right < containerBounds.right &&
    containedBounds.top > containerBounds.top &&
    containedBounds.bottom < containerBounds.bottom
  );
  
  isGroupPhysicallyContainedCache.set(key, result);
  return result;
};

/**
 * グループのプレースホルダー名を計算する
 * GROUP_COLORSの順、SUBTOTALの高い順に基づいて「Group 1」「Group 2」のようなプレースホルダー名を設定する
 * TOTAL表示のグループの場合はシート名をプレースホルダーとして使用する
 * @param groups 全グループの配列
 * @param pouches 全パウチの配列
 * @param sheetTitle シート名（TOTAL表示のグループのプレースホルダー用）
 * @returns プレースホルダー名が設定された新しいグループの配列
 */
export const calculateGroupPlaceholders = (
  groups: Group[],
  pouches: Pouch[],
  sheetTitle?: string
): Group[] => {
  if (!groups || groups.length === 0) return [];
  
  // グループをGROUP_COLORSの配列順にソート
  const sortedGroups = [...groups].sort((a, b) => {
    // 色でソート（GROUP_COLORSの配列順）
    const colorIndexA = GROUP_COLORS.findIndex(c => c.id === a.color);
    const colorIndexB = GROUP_COLORS.findIndex(c => c.id === b.color);
    if (colorIndexA !== colorIndexB) {
      return colorIndexA - colorIndexB;
    }
    
    // 色が同じ場合はタイトル昇順、タイトルがない場合はサブトータルの高い順にソート
    const aTitle = a.title?.trim() || '';
    const bTitle = b.title?.trim() || '';
    
    // 両方ともタイトルがある場合はタイトル昇順、同じ場合はサブトータル降順
    if (aTitle && bTitle) {
      const titleCompare = aTitle.localeCompare(bTitle, 'ja');
      if (titleCompare !== 0) {
        return titleCompare;
      }
      // タイトルが同じ場合はサブトータルの高い順
      const subtotalA = calculateGroupSubtotal(a.id, pouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), groups);
      const subtotalB = calculateGroupSubtotal(b.id, pouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), groups);
      return Math.abs(subtotalB) - Math.abs(subtotalA);
    }
    
    // 片方だけタイトルがある場合は、タイトルがある方を先に
    if (aTitle && !bTitle) return -1;
    if (!aTitle && bTitle) return 1;
    
    // 両方ともタイトルがない場合はサブトータルの高い順
    const subtotalA = calculateGroupSubtotal(a.id, pouches.filter(p => a.pouches?.includes(p.id) && !p._isDeleting), groups);
    const subtotalB = calculateGroupSubtotal(b.id, pouches.filter(p => b.pouches?.includes(p.id) && !p._isDeleting), groups);
    return Math.abs(subtotalB) - Math.abs(subtotalA);
  });
  
  // ソートされた順にプレースホルダー名を設定
  return sortedGroups.map((group, index) => {
    // 既にタイトルがある場合はそのままにする
    if (group.title && group.title.trim() !== '') {
      return group;
    }
    
    // TOTAL表示の条件を満たす場合はシート名をプレースホルダーとして使用
    if (shouldShowTotalLabel(group, groups, pouches) && sheetTitle) {
      return {
        ...group,
        placeholderText: sheetTitle
      };
    }
    
    // プレースホルダー名を設定（シリアルナンバー型）
    return {
      ...group,
      placeholderText: `Group ${getGroupSerialNumber(group.id, groups)}`
    };
  });
};

/**
 * グループのインデックスを一貫して計算する関数
 * @param groupId 対象のグループID
 * @param groups グループの配列
 * @returns グループのインデックス（1から開始）
 */
export const getGroupIndex = (groupId: string, groups: Group[]): number => {
  const index = groups.findIndex(g => g.id === groupId);
  return index + 1; // 1から開始するインデックス
};

/**
 * グループのシリアルナンバーを計算する関数
 * シート内で1から連番で付番し、削除しても欠番にする
 * @param groupId 対象のグループID
 * @param groups グループの配列
 * @returns グループのシリアルナンバー
 */
export const getGroupSerialNumber = (groupId: string, groups: Group[]): number => {
  // グループをID順でソート（z-indexはDOM操作で管理するため、ID順を使用）
  const sortedGroups = [...groups].sort((a, b) => a.id.localeCompare(b.id));
  
  // 対象グループの位置を取得
  const index = sortedGroups.findIndex(g => g.id === groupId);
  return index + 1; // 1から開始するシリアルナンバー
};

/**
 * グループの内容をテキスト形式に変換する関数
 * @param groupId グループID
 * @param pouches パウチの配列
 * @param groups グループの配列
 * @param sheets シートの配列
 * @param activeSheetId アクティブなシートID
 * @returns テキスト形式に変換された内容
 */
export const generateGroupShareText = (
  groupId: string,
  pouches: Array<{ 
    id: string; 
    value: number; 
    isNegative: boolean; 
    multiplier: number;
    color?: string;
    title?: string;
    _isDeleting?: boolean;
  }>,
  groups: Group[],
  sheets: Sheet[],
  activeSheetId: string
): string => {
  const group = groups.find(g => g.id === groupId);
  if (!group) return '';
  
  // グループ内のパウチを取得
  const groupPouches = pouches.filter(p => 
    group.pouches && group.pouches.includes(p.id) && !p._isDeleting
  );
  if (groupPouches.length === 0) return '';
  
  // 現在のシートを取得
  const sheet = sheets.find(s => s.id === activeSheetId);
  if (!sheet) return '';
  
  // 結果のテキスト
  let resultText = '';
  
  // 先頭にグループタイトルを追加（存在する場合のみ）
  if (group.title && group.title.trim() !== '') {
    resultText += `${group.title}\n-------------\n`;
  }
  // 色ごとに分類したパウチを作成
  const pouchesByColor: Record<string, typeof groupPouches> = {};
  
  groupPouches.forEach(pouch => {
    const colorId = pouch.color || GROUP_COLORS[0].id;
    if (!pouchesByColor[colorId]) {
      pouchesByColor[colorId] = [];
    }
    pouchesByColor[colorId].push(pouch);
  });
  
  // 合計ポイント数カウント用
  let totalPouchCount = 0;
  
  // シートの凡例順に表示
  (sheet.colorLegends || []).forEach(colorLegend => {
    const colorId = colorLegend.colorId;
    const colorPouches = pouchesByColor[colorId];
    
    // この色のパウチが存在しない場合はスキップ
    if (!colorPouches || colorPouches.length === 0) return;
    
    // 凡例のテキスト（空の場合はプレースホルダー）
    const legendText = colorLegend.legend && colorLegend.legend.trim() !== '' 
      ? colorLegend.legend + '\n'
      : '';
    
    // 凡例ヘッダーを追加
    resultText += `${legendText}`;
    
    // パウチを値、乗数、符号でグループ化
    // キーは "値_乗数_符号" の形式
    interface GroupedItem {
      value: number;
      multiplier: number;
      isNegative: boolean;
      count: number;
      title?: string;
    }
    
    const groups: { [key: string]: GroupedItem } = {};
    
    // パウチをグループ化
    colorPouches.forEach(pouch => {
      const key = `${pouch.value}_${pouch.multiplier}_${pouch.isNegative}_${pouch.title || ''}`;
      
      if (!groups[key]) {
        groups[key] = {
          value: pouch.value,
          multiplier: pouch.multiplier,
          isNegative: pouch.isNegative,
          count: 0,
          title: pouch.title
        };
      }
      
      groups[key].count++;
    });
    
    // グループ化したパウチを表示
    Object.values(groups).forEach(item => {
      // 各パウチの実際の数をカウントに追加
      if (item.multiplier > 1) { 
        totalPouchCount += item.multiplier * item.count; 
      } else { 
        totalPouchCount += item.count; 
      }
      
      const formattedValue = item.value.toLocaleString();
      
      // マルチプライヤー表示用の値とシンボルを計算（tableの表示ロジックと同じ）
      const getMultiplierDisplay = (multiplier: number) => {
        const absMultiplier = Math.abs(multiplier);
        // 絶対値が1以上、または整数の場合は通常表示
        if (absMultiplier >= 1 || absMultiplier === Math.floor(absMultiplier)) {
          return {
            value: absMultiplier,
            symbol: '×',
            isDivision: multiplier < 0,
            showSymbolAfter: false  // ×は数字の前に表示
          };
        }
        // 絶対値が1未満で小数の場合はパーセント表示
        const percentage = absMultiplier * 100; // Math.roundを削除して小数を保持
        return {
          value: percentage,
          symbol: '%',
          isDivision: multiplier < 0,
          showSymbolAfter: true  // %は数字の後に表示
        };
      };

      const multiplierDisplay = getMultiplierDisplay(item.multiplier);
      
      if (item.multiplier >= 0) { // 通常の乗算
        if (item.multiplier !== 1) { // 乗数が1でない場合
          // パーセント表示の場合は×を前に付ける
          const multiplierText = multiplierDisplay.showSymbolAfter 
            ? `× ${multiplierDisplay.value}${multiplierDisplay.symbol}`
            : `${multiplierDisplay.symbol} ${multiplierDisplay.value}`;
          resultText += `• ${item.title ? `${item.title}\n   ` : ''}${item.isNegative ? '-' : ''}${formattedValue} ${multiplierText}\n`;
          const subtotal = item.value * item.multiplier * (item.isNegative ? -1 : 1);
          resultText += `  = ${subtotal.toLocaleString()}\n`;
        } else if (item.count > 1) { // 乗数が1で複数ある場合
          resultText += `• ${item.title ? `${item.title}\n   ` : ''}${item.isNegative ? '-' : ''}${formattedValue} × ${item.count}\n`;
          const subtotal = item.value * item.count * (item.isNegative ? -1 : 1);
          resultText += `  = ${subtotal.toLocaleString()}\n`;
        } else { // 乗数が1で1つしかない場合
          resultText += `• ${item.title ? `${item.title}\n   ` : ''}${item.isNegative ? '-' : ''}${formattedValue}\n`;
        }
      } else { // 割り算
        const divisor = Math.abs(item.multiplier);
        // パーセント表示の場合は/を前に付ける
        const multiplierText = multiplierDisplay.showSymbolAfter 
          ? `/ ${multiplierDisplay.value}${multiplierDisplay.symbol}`
          : `${multiplierDisplay.symbol} ${multiplierDisplay.value}`;
        resultText += `• ${item.title ? `${item.title}\n   ` : ''}${item.isNegative ? '-' : ''}${formattedValue} ${multiplierText}\n`;
        
        // 計算結果
        const result = item.value / divisor * (item.isNegative ? -1 : 1);
        
        // 小数点以下は最大2桁まで表示
        const formattedResult = result % 1 === 0 
          ? result.toLocaleString() 
          : result.toFixed(2);
          
        resultText += `  = ${formattedResult}\n`;
      }
    });
    
    resultText += `-------------\n`;
  });
  
  // 合計の計算
  const total = calculateGroupSubtotal(groupId, groupPouches, groups);
  
  // 小数点以下は最大2桁まで表示
  const formattedTotal = total % 1 === 0 
    ? total.toLocaleString() 
    : total.toFixed(2);
  
  resultText += `TOTAL ${totalPouchCount} Item${totalPouchCount > 1 ? 's' : ''}\n= ${formattedTotal}\n`;
  return resultText;
};

/**
 * グループ内容を共有する
 * @param groupId 共有するグループのID
 * @param pouches パウチの配列
 * @param groups グループの配列
 * @param sheets シートの配列
 * @param activeSheetId アクティブなシートID
 * @returns 共有に成功したかどうか
 */
export const shareGroupContent = async (
  groupId: string,
  pouches: Array<{ 
    id: string; 
    value: number; 
    isNegative: boolean; 
    multiplier: number;
    color?: string;
    title?: string;
    _isDeleting?: boolean;
  }>,
  groups: Group[],
  sheets: Sheet[],
  activeSheetId: string
): Promise<boolean> => {
  // グループ内容のテキストを生成
  const shareText = generateGroupShareText(groupId, pouches, groups, sheets, activeSheetId);
  if (!shareText) return false;

  const isMobile = navigator.userAgent.match(/iPhone|iPad|iPod|Android/i);
  
  if (isMobile) {
    // モバイルの場合はWeb Share APIのみを使用
    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText,
        });
        return true;
      } catch (error) {
        console.error('Web Share APIでの共有に失敗しました', error);
        return false;
      }
    }
    return false;
  } else {
    // PCの場合
    if (navigator.share) {
      try {
        // 先にクリップボードにコピー
        await navigator.clipboard.writeText(shareText);
        await navigator.share({
          text: shareText,
        });
        return true;
      } catch (error) {
        console.error('Web Share APIでの共有に失敗しました', error);
        return true; // クリップボードにはコピー済みなのでtrueを返す
      }
    } else {
      // Web Share APIが利用できない場合はクリップボードにコピー
      try {
        await navigator.clipboard.writeText(shareText);
        return true;
      } catch (error) {
        console.error('クリップボードへのコピーに失敗しました', error);
        return false;
      }
    }
  }
};

// ミュートグループかどうかを判定する関数を追加
export const isMuteGroup = (group: Group): boolean => {
  return group.color === 'mute';
};

// グループがミュートグループに所属しているかを判定する関数を追加
export const isInMuteGroup = (pouchId: string, groups: Group[]): boolean => {
  return groups.some(group => 
    group.pouches?.includes(pouchId) && (
      isMuteGroup(group) || // 直接所属するグループがミュート
      groups.some(parentGroup => // 親グループがミュート
        parentGroup.pouches?.includes(group.id) && 
        isMuteGroup(parentGroup)
      )
    )
  );
  return false;
};

/**
 * GTP は isTime を持たないが、参照先の非GTPパウチがすべて時間ならシート上は時間として扱う（総合計の h:mm 等）
 */
export const pouchCountsAsTimeForSheetDisplay = (
  pouch: Pouch,
  allPouches: Pouch[],
  groups: Group[]
): boolean => {
  if (pouch.isGroupTotalPouch && pouch.referencedGroupId) {
    const referencedGroup = groups.find(g => g.id === pouch.referencedGroupId);
    if (!referencedGroup) {
      return false;
    }
    const nonGtpPouches = allPouches.filter(p =>
      referencedGroup.pouches?.includes(p.id) &&
      !p._isDeleting &&
      !p.isGroupTotalPouch &&
      !groups.some(g => g.color === 'mute' && g.pouches?.includes(p.id))
    );
    return nonGtpPouches.length > 0 && nonGtpPouches.every(p => p.isTime === true);
  }
  return pouch.isTime === true;
};

/**
 * 合計に含まれるパウチがすべて「時間表示」相当か（ミュート・削除中は除外）
 */
export const sheetContributingPouchesAreAllTime = (
  pouches: Pouch[],
  groups: Group[]
): boolean => {
  const contributing = pouches.filter(
    p => !p._isDeleting && !isInMuteGroup(p.id, groups)
  );
  if (contributing.length === 0) {
    return false;
  }
  return contributing.every(p => pouchCountsAsTimeForSheetDisplay(p, pouches, groups));
};

/**
 * グループがTOTAL表示の条件を満たすかを判定する関数
 * 条件：
 * 1. ミュートグループのパウチを除くすべてのパウチが所属している
 * 2. 他のグループに内包していない
 * 3. ミュートグループのパウチを除くすべてのパウチが所属している別のグループがない
 * @param group 対象のグループ
 * @param allGroups 全グループの配列
 * @param allPouches 全パウチの配列
 * @returns TOTAL表示すべきかどうか
 */
// キャッシュ用のMap
const shouldShowTotalLabelCache = new Map<string, boolean>();
// キャッシュキーにgroup.pouchesを含めることで、リサイズ終了時（pouches更新時）にキャッシュが無効化される
// group.pathは含めない（リサイズ中の頻繁な更新を防ぐ）
const cacheKey = (group: Group, allGroups: Group[], allPouches: Pouch[]) => 
  `${group.id}-${group.pouches?.join(',')}-${allGroups.length}-${allPouches.length}-${allGroups.map(g => `${g.id}:${g.pouches?.join('|')}`).join(',')}-${allPouches.map(p => p.id).join(',')}`;

// パウチが物理的にグループ内に含まれるかを判定するヘルパー関数
const isPouchPhysicallyInGroup = (pouch: Pouch, group: Group): boolean => {
  if (pouch.x === undefined || pouch.y === undefined) return false;
  const bounds = getPathBounds(group.path);
  if (!bounds) return false;
  return pouch.x >= bounds.left && 
         pouch.x <= bounds.right && 
         pouch.y >= bounds.top && 
         pouch.y <= bounds.bottom;
};

export const shouldShowTotalLabel = (
  group: Group,
  allGroups: Group[],
  allPouches: Pouch[]
): boolean => {
  // キャッシュキーを生成
  const key = cacheKey(group, allGroups, allPouches);
  
  // キャッシュから結果を取得
  if (shouldShowTotalLabelCache.has(key)) {
    return shouldShowTotalLabelCache.get(key)!;
  }
  
  // ミュートグループのパウチを除く有効なパウチを取得
  const validPouches = allPouches.filter(pouch => 
    !pouch._isDeleting && !isInMuteGroup(pouch.id, allGroups)
  );
  
  // 条件1: ミュートグループのパウチを除くすべてのパウチが物理的に所属している
  // group.pouchesではなく、物理的な位置で判定する（リサイズ時の状態更新遅延問題を回避）
  const groupPouches = validPouches.filter(pouch => 
    isPouchPhysicallyInGroup(pouch, group)
  );
  
  if (groupPouches.length !== validPouches.length) {
    shouldShowTotalLabelCache.set(key, false);
    return false;
  }
  
  // 条件2: 他のグループに内包していない
  const isContainedByOther = allGroups.some(otherGroup => {
    if (otherGroup.id === group.id) return false;
    return isGroupPhysicallyContained(group.id, otherGroup.id, allGroups);
  });
  
  if (isContainedByOther) {
    shouldShowTotalLabelCache.set(key, false);
    return false;
  }
  
  // 条件3: ミュートグループのパウチを除くすべてのパウチが物理的に所属している別のグループがない（内包グループを除く）
  const otherGroupsWithAllPouches = allGroups.filter(otherGroup => {
    if (otherGroup.id === group.id) return false;
    
    // 内包グループは除外
    if (isGroupPhysicallyContained(otherGroup.id, group.id, allGroups)) {
      return false;
    }
    
    // 物理的な位置で判定
    const otherGroupPouches = validPouches.filter(pouch => 
      isPouchPhysicallyInGroup(pouch, otherGroup)
    );
    
    return otherGroupPouches.length === validPouches.length;
  });
  
  if (otherGroupsWithAllPouches.length > 0) {
    shouldShowTotalLabelCache.set(key, false);
    return false;
  }
  
  shouldShowTotalLabelCache.set(key, true);
  return true;
}; 